import {
  dialogLabel,
  remedyCopyFor,
  type ImportDiagnostic,
} from "../../lib/importDiagnostics";

// Blocking state, so this renders INLINE and PERSISTENT -- not a toast. The user
// has to work through a procedure in another application and come back; a
// message that disappears on a timer is useless for that.

function MissingColumns({ diagnostic }: { diagnostic: ImportDiagnostic }) {
  if (diagnostic.missing.length === 0) return null;
  // Rendered by the component, not by the catalogue entry, so the actionable
  // part survives even when the remedy is unknown and the copy falls back. A
  // procedure with no target is not actionable.
  return (
    <p className="mt-3 text-sm text-on-surface">
      <span className="font-medium">Missing columns:</span>{" "}
      {diagnostic.missing.map(dialogLabel).join(", ")}
    </p>
  );
}

function DiagnosticBody({
  diagnostic,
  onRebindAccountMask,
}: {
  diagnostic: ImportDiagnostic;
  onRebindAccountMask?: () => void;
}) {
  const copy = remedyCopyFor(diagnostic.remedy);

  if (copy === undefined) {
    // Unknown remedy -- a backend check shipped without its copy. Render the
    // server's own message rather than an empty box, which is the failure mode
    // that turns a helpful error into a dead end.
    // "Import blocked" only when it IS blocked. The warn severity became
    // reachable here with the stale-statement warning (ADR 0039 D9.6), and
    // labelling an advisory as a block would overstate it in the one case where
    // the copy has already failed to load.
    return (
      <>
        <p className="text-sm font-medium text-on-surface">
          {diagnostic.severity === "block" ? "Import blocked" : "Check this statement"}
        </p>
        {diagnostic.message !== "" && (
          <p className="mt-1 whitespace-pre-line text-sm text-on-surface-variant">
            {diagnostic.message}
          </p>
        )}
        <MissingColumns diagnostic={diagnostic} />
        <p className="mt-3 text-sm text-on-surface-variant">
          If this keeps happening, contact support and quote{" "}
          <code className="font-mono text-xs">{diagnostic.code}</code>.
        </p>
      </>
    );
  }

  const steps = copy.steps(diagnostic);
  return (
    <>
      <p className="text-sm font-medium text-on-surface">{copy.title}</p>
      <p className="mt-1 text-sm text-on-surface-variant">{copy.explanation(diagnostic)}</p>
      <MissingColumns diagnostic={diagnostic} />
      {steps.length > 0 && (
        <>
          <p className="mt-3 text-sm font-medium text-on-surface">
            To fix it — about 30 seconds:
          </p>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-on-surface-variant">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </>
      )}
      {copy.footnote !== undefined && (
        <p className="mt-3 text-sm text-on-surface-variant">{copy.footnote}</p>
      )}
      {copy.action?.kind === "rebind-account-mask" && onRebindAccountMask !== undefined && (
        <button
          type="button"
          onClick={onRebindAccountMask}
          data-testid="rebind-account-mask"
          className="mt-3 rounded-control border border-outline px-3 py-1.5 text-sm font-medium text-on-surface hover:bg-surface-container"
        >
          {copy.action.label}
        </button>
      )}
    </>
  );
}

export function ImportDiagnosticsAlert({
  diagnostics,
  onRebindAccountMask,
}: {
  diagnostics: readonly ImportDiagnostic[];
  /**
   * Supplied only where a re-link can actually be performed. Omitted (so the
   * button does not render) where the diagnostic is read back from a finished
   * job and the statement file is no longer in hand -- offering an action that
   * cannot complete is worse than not offering it.
   */
  onRebindAccountMask?: () => void;
}) {
  if (diagnostics.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="import-diagnostics">
      {diagnostics.map((diagnostic) => (
        <div
          key={`${diagnostic.code}:${diagnostic.section}`}
          className={
            diagnostic.severity === "block"
              ? "rounded-control border border-error/40 bg-error/5 p-4"
              : "rounded-control border border-outline bg-surface-container p-4"
          }
        >
          <DiagnosticBody
            diagnostic={diagnostic}
            onRebindAccountMask={onRebindAccountMask}
          />
        </div>
      ))}
    </div>
  );
}
